import { useState, useEffect } from 'react';

interface CurrencyConfig {
  symbol: string;
  code: string;
  rate: number; // Exchange rate from USD base
}

const currencyMap: Record<string, CurrencyConfig> = {
  USD: { symbol: '$', code: 'USD', rate: 1 }, // US Dollar (base)
  IRR: { symbol: '﷼', code: 'IRR', rate: 42000 }, // Iranian Rial
  AMD: { symbol: '֏', code: 'AMD', rate: 385 }, // Armenian Dram
  RUB: { symbol: '₽', code: 'RUB', rate: 75 } // Russian Ruble
};

// Create a custom event for currency changes
const CURRENCY_CHANGE_EVENT = 'currencyChange';

export const useCurrency = () => {
  const [currentCurrency, setCurrentCurrency] = useState<CurrencyConfig>(() => {
    const saved = localStorage.getItem('selectedCurrency');
    return saved ? currencyMap[saved] || currencyMap.USD : currencyMap.USD;
  });
  
  useEffect(() => {
    // Listen for currency changes from other components
    const handleCurrencyChange = (event: CustomEvent) => {
      const newCurrency = currencyMap[event.detail] || currencyMap.USD;
      setCurrentCurrency(newCurrency);
    };

    window.addEventListener(CURRENCY_CHANGE_EVENT as any, handleCurrencyChange);
    
    return () => {
      window.removeEventListener(CURRENCY_CHANGE_EVENT as any, handleCurrencyChange);
    };
  }, []);
  
  const setCurrency = (currencyCode: string) => {
    const newCurrency = currencyMap[currencyCode] || currencyMap.USD;
    setCurrentCurrency(newCurrency);
    localStorage.setItem('selectedCurrency', currencyCode);
    
    // Dispatch custom event to notify all components
    window.dispatchEvent(new CustomEvent(CURRENCY_CHANGE_EVENT, { detail: currencyCode }));
  };

  const formatPrice = (usdPrice: number): string => {
    const convertedPrice = usdPrice * currentCurrency.rate;
    
    // Format based on currency
    switch (currentCurrency.code) {
      case 'IRR':
        return `${Math.round(convertedPrice).toLocaleString('fa-IR')} ${currentCurrency.symbol}`;
      case 'AMD':
        return `${Math.round(convertedPrice).toLocaleString('hy-AM')} ${currentCurrency.symbol}`;
      case 'RUB':
        return `${Math.round(convertedPrice).toLocaleString('ru-RU')} ${currentCurrency.symbol}`;
      default:
        return `${currentCurrency.symbol}${convertedPrice.toFixed(2)}`;
    }
  };
  
  return {
    formatPrice,
    currency: currentCurrency,
    setCurrency
  };
};
